import { memo, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import {
  BrowserThin,
  PlusCircle,
} from "@hl/base-components/lib/assets/icons.generated";
import { ColorSchemeMode } from "@hl/base-components/lib/theme/ColorSchemeContext";
import {
  OUTLINE_COLOR,
  PRIMARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { AppSEO } from "@hl/shared-features/lib/features/layout/AppSEO";
import usePageVisitedEvent from "@hl/shared-features/lib/hooks/usePageVisitedEvent";
import { Button, Text, Group, Divider, useMantineTheme } from "@mantine/core";
import { Link } from "react-router-dom";

import { TOOLS_URL } from "config";
import CreateFirstV2 from "~/features/dashboard/CreateFirstV2";
import DashboardContent from "~/features/dashboard/DashboardContent";

import CustomPaginationWrapper from "../tokens/components/CustomPaginationWrapper";

import HighlightPage from "./HighlightPage";
import { usePagesQuery } from "./queries/page.graphql.generated";

const PagesDashboardV2 = () => {
  const theme = useMantineTheme();

  const [activePage, setPage] = useState(1);
  const [lowerBound, setLowerBound] = useState(1);
  const [upperBound, setUpperBound] = useState(1);

  const { data, loading } = usePagesQuery({
    errorPolicy: "all",
    fetchPolicy: "network-only",
  });

  usePageVisitedEvent("PAGES");

  const pages = data?.pages ?? [];

  return (
    <DashboardContent
      header={
        <Group>
          <Button
            component={Link}
            color={
              theme.colorScheme === ColorSchemeMode.LIGHT
                ? PRIMARY_COLOR
                : OUTLINE_COLOR
            }
            to={TOOLS_URL.pages.create}
            leftIcon={<PlusCircle />}
            size="xl"
          >
            Create a page
          </Button>
        </Group>
      }
    >
      <AppSEO title="Pages" />
      {loading ? (
        <LoadingPage />
      ) : pages.length === 0 ? (
        <CreateFirstV2
          entity="page"
          redirectUrl={TOOLS_URL.pages.create}
          header={<BrowserThin width={64} height={64} />}
        />
      ) : (
        <CustomPaginationWrapper
          itemsPerPage={10}
          activePage={activePage}
          setPage={setPage}
          containerStyle={
            pages?.length === 1 ? { overflow: "visible" } : undefined
          }
          setLowerBound={setLowerBound}
          setUpperBound={setUpperBound}
          maxLength={pages.length}
        >
          {[...pages]
            .reverse()
            .slice(lowerBound - 1, upperBound)
            .map((page, index) => {
              return (
                <div key={page.id}>
                  <Text
                    component={Link}
                    to={TOOLS_URL.pages.edit.replace(":pageId", page.id)}
                  >
                    <HighlightPage
                      key={page.id}
                      id={page.id}
                      name={page.name}
                      slug={page.slug}
                      created={page.created}
                      gateId={page.gate ? page.gate.id : undefined}
                      gateName={
                        page.gate ? page.gate.name ?? undefined : undefined
                      }
                    />
                  </Text>
                  {index < upperBound - lowerBound && <Divider />}
                </div>
              );
            })}
        </CustomPaginationWrapper>
      )}
    </DashboardContent>
  );
};

export default memo(PagesDashboardV2);
